import { Injectable, inject } from '@angular/core';
import { Client, StompSubscription, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiEnvironmentService } from '../settings/api-environment.service';
import { LoggerService } from '../logger/logger.service';
import { MaintenanceService } from '../maintenance/maintenance.service';

export enum ConnectionStatus {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR'
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private readonly apiEnvironmentService: ApiEnvironmentService = inject(ApiEnvironmentService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly maintenanceService: MaintenanceService = inject(MaintenanceService);
  private client: Client | null = null;
  private subscriptions: Map<string, StompSubscription> = new Map<string, StompSubscription>();
  private connectionStatusSubject: BehaviorSubject<ConnectionStatus> = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private lastToken?: string;

  public readonly connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor() {
    this.setupMobileEventListeners();
  }

  private setupMobileEventListeners(): void {
    // Handle app going to background/foreground (important for mobile PWAs)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.lastToken) {
        // App came back to foreground, reconnect if needed
        if (!this.client || !this.client.connected) {
          this.connect(this.lastToken).catch(() => {
            // Silent reconnection attempt on resume
          });
        }
      }
    });

    // Handle network status changes (mobile often switches networks)
    window.addEventListener('online', () => {
      if (this.lastToken && (!this.client || !this.client.connected)) {
        this.connect(this.lastToken).catch(() => {
          // Silent reconnection attempt when online
        });
      }
    });
  }

  connect(token?: string): Promise<void> {
    if (this.client && this.client.connected) {
      return Promise.resolve();
    }

    if (this.maintenanceService.isActive()) {
      this.lastToken = token;
      return Promise.reject(new Error('WebSocket connection deferred: backend is in maintenance mode'));
    }

    // Store token for reconnection attempts
    this.lastToken = token;

    return new Promise<void>((resolve, reject) => {
      this.connectionStatusSubject.next(ConnectionStatus.CONNECTING);

      this.client = new Client({
        webSocketFactory: (): WebSocket => {
          const wsUrl: string = this.getWebSocketUrl();
          return new SockJS(wsUrl) as WebSocket;
        },
        connectHeaders: token ? {
          'Authorization': `Bearer ${token}`
        } : {},
        debug: () => {
          // Disable STOMP debug logs
        },
        reconnectDelay: this.getReconnectDelay(),
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        onConnect: () => {
          this.connectionStatusSubject.next(ConnectionStatus.CONNECTED);
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          resolve();
        },
        onDisconnect: () => {
          this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
        },
        onStompError: (frame) => {
          this.connectionStatusSubject.next(ConnectionStatus.ERROR);
          reject(new Error(frame.headers['message'] || 'WebSocket connection error'));
        },
        onWebSocketError: (error) => {
          this.connectionStatusSubject.next(ConnectionStatus.ERROR);
          reject(error);
        },
        onWebSocketClose: () => {
          this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
        }
      });

      this.client.activate();
    });
  }

  disconnect(): void {
    if (this.client) {
      this.subscriptions.forEach((subscription: StompSubscription) => {
        subscription.unsubscribe();
      });
      this.subscriptions.clear();
      this.client.deactivate();
      this.client = null;
      this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
    }
  }

  subscribe<T = unknown>(destination: string, callback: (message: T) => void): string {
    if (!this.client || !this.client.connected) {
      throw new Error('WebSocket is not connected. Call connect() first.');
    }

    if (this.subscriptions.has(destination)) {
      this.unsubscribe(destination);
    }

    const subscription: StompSubscription = this.client.subscribe(destination, (message: IMessage) => {
      try {
        const parsedMessage: T = JSON.parse(message.body) as T;
        callback(parsedMessage);
      } catch (error: unknown) {
        this.logger.error('WebSocket: Failed to parse message', error);
      }
    });

    this.subscriptions.set(destination, subscription);
    return destination;
  }

  unsubscribe(destination: string): void {
    const subscription: StompSubscription | undefined = this.subscriptions.get(destination);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(destination);
    }
  }

  isConnected(): boolean {
    return this.client !== null && this.client.connected;
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  private getWebSocketUrl(): string {
    try {
      return this.apiEnvironmentService.getCurrentWsUrl();
    } catch {
      return environment.wsApiUrl;
    }
  }

  private getReconnectDelay(): number {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return 0;
    }
    
    const delay: number = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;
    return Math.min(delay, 30000);
  }
}
